import React, { useState, useRef } from 'react';
import { useAuthStore } from '../../lib/store-auth';
import { supabase } from '../../lib/supabase';
import { ProfileAvatar } from './ProfileAvatar';
import './profile.scss';

interface ProfileSettingsProps {
    onClose: () => void;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({ onClose }) => {
    const { user, updateProfile } = useAuthStore();
    const [fullName, setFullName] = useState(user?.full_name || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !user) return;

        // Dosya boyutu kontrolü (2MB)
        if (file.size > 2 * 1024 * 1024) {
            setError('Dosya boyutu 2MB\'dan küçük olmalıdır.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${user.id}-${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Eski avatarı sil (opsiyonel, şimdilik atlıyorum)

            // Yeni avatarı yükle
            const { error: uploadError } = await supabase!.storage
                .from('avatars')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            // Public URL al
            const { data: { publicUrl } } = supabase!.storage
                .from('avatars')
                .getPublicUrl(filePath);

            // Profili güncelle
            const { error: updateError } = await supabase!
                .from('profiles')
                .update({ avatar_url: publicUrl })
                .eq('id', user.id);

            if (updateError) throw updateError;

            // State'i güncelle
            updateProfile({ avatar_url: publicUrl });

        } catch (err: any) {
            console.error('Avatar yükleme hatası:', err);
            setError(err.message || 'Avatar yüklenirken bir hata oluştu.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!user) return;

        setIsLoading(true);
        setError(null);

        try {
            // İsim güncelle
            const { error: updateError } = await supabase!
                .from('profiles')
                .update({ full_name: fullName })
                .eq('id', user.id);

            if (updateError) throw updateError;

            // State'i güncelle
            updateProfile({ full_name: fullName });
            onClose();

        } catch (err: any) {
            console.error('Profil güncelleme hatası:', err);
            setError(err.message || 'Profil güncellenirken bir hata oluştu.');
            setIsLoading(false);
        }
    };

    if (!user) return null;

    return (
        <div className="profile-settings-overlay" onClick={onClose}>
            <div className="profile-settings-modal" onClick={e => e.stopPropagation()}>
                <h2>Profil Ayarları</h2>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-3 rounded mb-4 text-sm">
                        {error}
                    </div>
                )}

                <div className="avatar-upload-section">
                    <ProfileAvatar
                        url={user.avatar_url}
                        name={fullName || user.email}
                        size="xl"
                    />

                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />

                    <button
                        className="upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Yükleniyor...' : 'Fotoğrafı Değiştir'}
                    </button>
                    <p className="text-xs text-gray-500">Maksimum 2MB (JPG, PNG)</p>
                </div>

                <div className="form-group">
                    <label>Ad Soyad</label>
                    <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Adınız Soyadınız"
                    />
                </div>

                <div className="form-group">
                    <label>E-posta</label>
                    <input
                        type="text"
                        value={user.email}
                        disabled
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    />
                </div>

                <div className="modal-actions">
                    <button className="btn-cancel" onClick={onClose} disabled={isLoading}>
                        İptal
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={isLoading}>
                        {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
};
